import apiV2 from "api-v2";
import type {
	ScoringInstruction,
	CreateScoringInstructionPayload,
	UpdateScoringInstructionPayload,
} from "../types/ScoringInstruction.type";

const BASE = "/scoring-instructions";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getScoringInstructions = async (): Promise<ScoringInstruction[]> => {
	const response = await apiV2.get(BASE);
	return unwrap<ScoringInstruction[]>(response.data) ?? [];
};

export const getScoringInstruction = async (uuid: string): Promise<ScoringInstruction> => {
	const response = await apiV2.get(`${BASE}/${uuid}`);
	return unwrap<ScoringInstruction>(response.data);
};

export const createScoringInstruction = async (
	payload: CreateScoringInstructionPayload
): Promise<ScoringInstruction> => {
	const response = await apiV2.post(BASE, payload);
	return unwrap<ScoringInstruction>(response.data);
};

export const updateScoringInstruction = async (
	uuid: string,
	payload: UpdateScoringInstructionPayload
): Promise<ScoringInstruction> => {
	const response = await apiV2.put(`${BASE}/${uuid}`, payload);
	return unwrap<ScoringInstruction>(response.data);
};

export const deleteScoringInstruction = async (uuid: string): Promise<void> => {
	await apiV2.delete(`${BASE}/${uuid}`);
};

export const exportScoringInstructions = async (): Promise<Blob> => {
	const response = await apiV2.get(`${BASE}/export`, { responseType: "blob" });
	return response.data as Blob;
};

export const getImportTemplate = async (): Promise<Blob> => {
	const response = await apiV2.get(`${BASE}/import-template`, { responseType: "blob" });
	return response.data as Blob;
};

export const importScoringInstructions = async (file: File): Promise<ScoringInstruction> => {
	const formData = new FormData();
	formData.append("file", file);
	const response = await apiV2.post(`${BASE}/import`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
	return unwrap<ScoringInstruction>(response.data);
};

export const validateScoringInstructionsImport = async (
	file: File
): Promise<{ valid: boolean; errors: string[]; preview: unknown[] }> => {
	const formData = new FormData();
	formData.append("file", file);
	const response = await apiV2.post(`${BASE}/import/validate`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
	return unwrap<{ valid: boolean; errors: string[]; preview: unknown[] }>(response.data);
};
